import { useCallback } from 'react';

import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import * as T from 'fp-ts/lib/Task';
import * as TE from 'fp-ts/lib/TaskEither';
import { useRecoilState, RecoilState } from 'recoil';

import { BSTaskFromIO } from '../../util/bs-fp';
import type { BSError } from '../error/BSError';
import type { BSTask } from '../types';

export interface AsyncAtomData<T> {
  data: T | null;
  error: BSError | null;
  received: boolean;
  updating: boolean;
}

export const useAsyncAtomState = <T>(atom: RecoilState<AsyncAtomData<T>>) => {
  const [state, updateState] = useRecoilState(atom);

  const execute = useCallback(
    async (
      asyncFn: () => BSTask<T>,
      onError?: (error: BSError) => void | Promise<void>,
      onSuccess?: (data: T) => void | Promise<void>
    ) => {
      const task = pipe(
        BSTaskFromIO(() => updateState((prev) => ({ ...prev, updating: true }))),
        TE.chain(() => asyncFn()),
        T.map(
          E.fold(
            async (error) => {
              await onError?.(error);
              updateState({ data: null, error, received: true, updating: false });
            },
            async (data) => {
              await onSuccess?.(data);
              updateState({ data, error: null, received: true, updating: false });
            }
          )
        )
      );

      await task();
    },
    [updateState]
  );

  return { ...state, execute };
};
